import { api, ApiError } from '@/lib/api';
import type {
  ForecastPoint,
  ForecastAnomaly,
  ForecastSensor,
  AnomalySummary,
  ForecastTriggerRequest,
  ForecastTriggerResponse,
} from '../types';

/**
 * Get the latest forecast for a sensor (returns [] when no forecast exists)
 */
export async function getLatestForecast(sensorId: string, organisationId?: string): Promise<ForecastPoint[]> {
  try {
    const response = await api.get<ForecastPoint[]>(`/v4/forecasts/${sensorId}`, { organisationId });
    return response || [];
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return [];
    }
    throw error;
  }
}

/**
 * Get historical forecasts for a sensor
 */
export async function getForecastHistory(
  sensorId: string,
  params?: { organisationId?: string; from?: string; to?: string; forecastType?: string; pageLength?: number },
): Promise<ForecastPoint[]> {
  const response = await api.get<ForecastPoint[]>(
    `/v4/forecasts/${sensorId}/history`,
    params,
  );
  return response || [];
}

/**
 * Trigger an on-demand forecast for a sensor
 */
export async function triggerForecast(
  sensorId: string,
  params?: ForecastTriggerRequest,
  organisationId?: string,
): Promise<ForecastTriggerResponse> {
  const query = organisationId ? `?organisationId=${encodeURIComponent(organisationId)}` : '';
  return api.post<ForecastTriggerResponse>(
    `/v4/forecasts/${sensorId}/trigger${query}`,
    params || {},
  );
}

/**
 * Get anomaly events with filtering
 */
export async function getAnomalies(params?: {
  organisationId?: string;
  sensorId?: string;
  from?: string;
  to?: string;
  anomalyType?: string;
  severity?: string;
  pageNumber?: number;
  pageLength?: number;
}): Promise<ForecastAnomaly[]> {
  const response = await api.get<ForecastAnomaly[]>('/v4/forecasts/anomalies', params);
  return response || [];
}

/**
 * Get anomaly summary counts
 */
export async function getAnomalySummary(params?: {
  organisationId?: string;
  from?: string;
  to?: string;
}): Promise<AnomalySummary> {
  return api.get<AnomalySummary>('/v4/forecasts/anomalies/summary', params);
}

/**
 * List forecastable sensors for an organisation, optionally filtered by search.
 */
export async function getForecastableSensors(
  organisationId: string,
  search = '',
): Promise<ForecastSensor[]> {
  const response = await api.get<{ sensors: ForecastSensor[] }>('/v4/forecasts/sensors', {
    organisationId,
    search,
    pageLength: 100,
  });
  return response.sensors || [];
}

export function searchSensors(organisationId: string, search = ''): Promise<ForecastSensor[]> {
  return getForecastableSensors(organisationId, search);
}
